import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCheck,FiArrowRight,FiCpu,FiMessageSquare,FiBarChart,FiSettings,FiCalendar}=FiIcons;

const IntelligenzaArtificiale=()=> {
  const services=[
    {
      title: "Chatbot AI",
      description: "Assistenti virtuali per customer service e lead generation automatizzata",
      icon: FiMessageSquare,
      features: ["Risposte automatiche","Lead qualification","Integrazione CRM"]
    },
    {
      title: "Automazioni Marketing",
      description: "Workflow automatizzati per email marketing e social media management",
      icon: FiSettings,
      features: ["Email automation","Social scheduling","Lead nurturing"]
    },
    {
      title: "Analisi Predittiva",
      description: "AI per analizzare dati e prevedere comportamenti clienti",
      icon: FiBarChart,
      features: ["Customer insights","Trend analysis","ROI prediction"]
    },
    {
      title: "Ottimizzazione AI",
      description: "Machine learning per ottimizzare campagne e performance",
      icon: FiCpu,
      features: ["A/B testing automatico","Bid optimization","Content optimization"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Intelligenza Artificiale Napoli – Automazioni e Chatbot per il Tuo Business</title>
        <meta name="description" content="Servizi AI per business a Napoli: chatbot intelligenti,automazioni marketing,analisi predittiva. Riduci i costi e aumenta l'efficienza." />
        <meta name="keywords" content="intelligenza artificiale napoli,chatbot napoli,automazioni marketing napoli,ai business napoli" />
        <link rel="canonical" href="https://realizzazionesitiweb-napoli.it/servizi/intelligenza-artificiale-napoli" />
      </Helmet>

      {/* Hero Section - Velo scuro al 40% */}
      <section className="relative min-h-screen bg-napoli-gradient text-white overflow-hidden flex items-center">
        <div className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 style={{
      paddingTop: '40px',
      paddingBottom: '40px'
    }}
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              className="text-4xl lg:text-5xl font-poppins font-bold mb-6 text-contrast-white"
            >Intelligenza Artificiale per il Business</motion.h1>
            
            <motion.p
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.2}}
              className="text-xl mb-8 text-contrast-white leading-relaxed"
            >
              Integriamo l'AI nel tuo business a Napoli per automatizzare processi,migliorare il customer service e ottimizzare le performance. Chatbot intelligenti,automazioni e analisi predittiva.
            </motion.p>
            
            <motion.div
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.4}}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contatti"
                className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-accent focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
              >
                Preventivo gratuito in 24h
              </Link>
              <a
                href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-azzurro focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" />
                Prenota una consulenza
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-6">
              Soluzioni AI per il tuo business
            </h2>
            <p className="text-xl text-napoli-blu font-medium">
              Automatizza,ottimizza e cresci con l'intelligenza artificiale
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: index * 0.1}}
                viewport={{once: true}}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-napoli-azzurro to-napoli-accent rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-napoli-blu" />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-napoli-blu mb-3">
                  {service.title}
                </h3>
                <p className="text-napoli-blu mb-4 leading-relaxed font-medium">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature,idx)=> (
                    <li key={idx} className="flex items-center text-sm text-napoli-blu font-medium">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-napoli-accent mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Velo scuro al 40% */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold mb-6 text-white drop-shadow-lg">
            Il futuro è già qui
          </h2>
          <p className="text-xl mb-8 text-white max-w-2xl mx-auto font-medium drop-shadow-md">
            Scopri come l'intelligenza artificiale può trasformare il tuo business e ridurre i costi operativi.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contatti"
              className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-accent focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              Preventivo gratuito in 24h
            </Link>
            <a
              href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-azzurro focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" />
              Prenota una consulenza
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default IntelligenzaArtificiale;